import { Input } from 'components/Input';
import { Link } from 'react-router-dom';
import { FC, memo, useCallback, useState, useRef, FormEvent } from 'react';
import { useValidatePassword } from 'hooks/useValidatePassword';
import { Box } from 'ui/Box';

import { Checkbox } from 'components/CheckBox';

import { Typography } from 'ui/Typography';

import { ButtonsSet } from 'modules/RegisterModule/components/ButtonsSet';
import { useBreakpoints } from 'hooks/useBreakpoints';
import { getTruncatedText } from 'modules/RegisterModule/helpers';

export interface Step6Props {
  title: string;
  prevStep: () => void;
  onSubmit: (password: string, email?: string) => void;
  error?: string;
  email?: boolean;
  disabled?: boolean;
}
export const Step6: FC<Step6Props> = memo(
  ({ title, error, disabled = false, email = false, prevStep, onSubmit }) => {
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [agreed, setAgreed] = useState(false);
    const [emailValue, setEmailValue] = useState('');
    const { isUpTablet } = useBreakpoints();
    const {
      validatePassword,
      passwordError,
      confirmPasswordError,
      emailError,
    } = useValidatePassword();
    const confirmPasswordRef = useRef<HTMLInputElement>(null);
    const handleCheckboxChange = () => setAgreed((prev) => !prev);

    const onNextClick = useCallback(async () => {
      if (!validatePassword(password, confirmPassword, emailValue, email))
        return;

      onSubmit(password, emailValue);
    }, [
      password,
      confirmPassword,
      emailValue,
      email,
      onSubmit,
      validatePassword,
    ]);

    return (
      <Box className="flex flex-1 flex-col items-center justify-center">
        <Typography
          variant="pSemiBold"
          className="mt-8 max-w-[44rem] text-center text-[28px] md:text-[32px]"
        >
          It's a pleasure to meet you {getTruncatedText(isUpTablet, title)}!
          Let's start with your password
        </Typography>
        <Box
          component="form"
          className="mt-8 flex w-full max-w-[40rem] items-center justify-center gap-5 sm:p-4"
        >
          <Box className="flex w-full flex-col gap-4">
            {email && (
              <Input
                tabIndex={0}
                placeholder="Email"
                type="email"
                value={emailValue}
                error={!!emailError}
                helperText={emailError}
                onChange={(event) => setEmailValue(event.target.value)}
              />
            )}
            <Input
              type="password"
              placeholder="Password"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              error={!!passwordError}
              helperText={passwordError}
              onKeyDown={(event) => {
                if (event.key === 'Enter') {
                  event.preventDefault();
                  confirmPasswordRef.current?.focus();
                }
              }}
            />
            <Input
              inputRef={confirmPasswordRef}
              type="password"
              placeholder="Confirm Password"
              value={confirmPassword}
              onChange={(event) => setConfirmPassword(event.target.value)}
              error={!!confirmPasswordError || !!error}
              helperText={confirmPasswordError || error}
              onKeyDown={(event) => {
                if (event.key === 'Enter') {
                  event.preventDefault();
                  onNextClick();
                }
              }}
            />

            <Box className="m-0 flex items-start gap-2 p-0 text-gray-700">
              <Checkbox checked={agreed} onClick={handleCheckboxChange} />
              <Typography
                variant="body2"
                component="span"
                className="cursor-default"
              >
                Agree with{' '}
                <Link
                  to="/privacy-policy"
                  target="_blank"
                  className="text-blue-500 text-blue hover:underline"
                >
                  Privacy Policy{' '}
                </Link>
                and{' '}
                <Link
                  to="/terms-and-conditions"
                  target="_blank"
                  className="text-blue-500 text-blue hover:underline"
                >
                  Terms and Conditions
                </Link>
              </Typography>
            </Box>
          </Box>
        </Box>
        <ButtonsSet
          onBack={prevStep}
          onNext={onNextClick}
          isNextDisabled={!agreed || disabled}
        />
      </Box>
    );
  }
);
